import { TextField, Stack, Fade } from '@mui/material';
import { useState, useRef, type KeyboardEvent, type ChangeEvent } from 'react';

interface OtpCodeProps {
  onChange: (code: string) => void;
  onConfirm?: () => void;
  hasError?: boolean;
  /** Slightly tighter cells for inline pairing rows. */
  compact?: boolean;
  /** Defaults to false so always-visible pairing (e.g. stage) does not open the mobile keyboard. */
  autoFocus?: boolean;
}

export default function OtpCode({ onChange, onConfirm, hasError, compact = false, autoFocus = false }: OtpCodeProps) {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const cellSize = compact ? 26 : 40;
  const fontSize = compact ? '0.95rem' : '1.5rem';
  const gap = compact ? 0.25 : 1;

  const focusDigit = (index: number) => {
    // preventScroll avoids mobile browsers re-centering the page on every digit.
    inputRefs.current[index]?.focus({ preventScroll: true });
  };

  const handleChange = (element: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
    const rawValue = element.target.value;
    const value = rawValue.replace(/\D/g, '');

    if (!value && rawValue !== '') return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < 5) {
      focusDigit(index + 1);
    }

    const otpCode = newOtp.join('');
    onChange(otpCode);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (event.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        focusDigit(index - 1);
      }
    } else if (event.key === 'Enter' && onConfirm) {
      // Check if OTP is complete (all 6 digits filled)
      const otpCode = otp.join('');
      if (otpCode.length === 6) {
        event.preventDefault();
        onConfirm();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text').replace(/\D/g, '');
    const otpArray = pastedData.slice(0, 6).split('');

    // Start from a clean array so leftover digits from a previous attempt are cleared.
    const newOtp = Array.from({ length: 6 }, (_, index) => otpArray[index] ?? '');

    setOtp(newOtp);
    onChange(newOtp.join(''));

    // Focus last filled input or first empty one
    const lastIndex = Math.min(otpArray.length - 1, 5);
    focusDigit(lastIndex);
  };

  return (
    <Fade in={true} timeout={800}>
      <Stack direction="row" spacing={gap} alignItems="center" sx={{ flexShrink: 0 }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(ref) => (inputRefs.current[index] = ref)}
            autoFocus={autoFocus && index === 0}
            error={hasError}
            variant="outlined"
            type="text"
            value={digit}
            // Avoid browser OTP autofill re-populating a previous pairing code.
            autoComplete="off"
            size="small"
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            sx={{
              'width': cellSize,
              'flexShrink': 0,
              '& .MuiOutlinedInput-root': {
                width: cellSize,
                height: cellSize,
              },
              '& .MuiOutlinedInput-input': {
                p: 0,
                textAlign: 'center',
                fontSize,
                height: cellSize,
                boxSizing: 'border-box',
              },
            }}
            slotProps={{
              htmlInput: {
                'maxLength': 1,
                'inputMode': 'numeric',
                'pattern': '[0-9]*',
                'data-testid': `otp-code-${index}`,
                'aria-label': `Digit ${index + 1}`,
              },
            }}
          />
        ))}
      </Stack>
    </Fade>
  );
}
